import React, { useState, useEffect, useRef } from 'react';
import Table from './Table';
import InfoPage from './InfoPage';

// Funktion zum Starten der Konfetti-Animation über einem bestimmten HTML-Container.
// Sie erstellt ein Canvas-Element, zeichnet Konfetti-Stücke und animiert deren Fall.
export function launchConfettiOverBox(canvas) {
  // Überprüft, ob das Canvas-Element existiert.
  if (!canvas) {
    console.error("Confetti canvas element is null.");
    return;
  }

  const ctx = canvas.getContext("2d");
  const container = canvas.parentNode; // Der übergeordnete Knoten ist der Quiz-Container.
  // Überprüft, ob der übergeordnete Container existiert.
  if (!container) {
    console.error("Confetti canvas has no parent container.");
    return;
  }

  const width = container.clientWidth; // Breite des Containers.
  const height = container.offsetHeight; // Höhe des Containers.
  canvas.width = width;
  canvas.height = height;
  canvas.style.display = "block";
  canvas.style.position = "absolute"; // Absolut positioniert innerhalb des Quiz-Containers.
  canvas.style.top = "0";
  canvas.style.left = "0";
  canvas.style.pointerEvents = "none"; // Lässt Klicks auf darunterliegende Elemente zu.
  canvas.style.zIndex = "1000"; // Stellt sicher, dass das Konfetti über anderen Elementen liegt.

  // Initialisiert 100 Konfetti-Stücke mit zufälligen Startpositionen, Größen, Farben und Bewegungen.
  const pieces = Array.from({ length: 100 }, () => ({
    x: Math.random() * width, // Zufällige X-Startposition.
    y: height - 10 + Math.random() * 10, // Startet knapp über dem unteren Rand.
    r: Math.random() * 6 + 2, // Radius des Konfetti-Stücks.
    color: ["#fad7d2", "#fce8dc", "#d6a77a", "#ebf2f7", "#8ecae6", "#ffb703", "#ff8fa3", "#caffbf"][Math.floor(Math.random() * 8)], // Zufällige Farbe aus definierter Palette.
    speed: Math.random() * 0.5 + 0.3, // Zufällige Fallgeschwindigkeit.
    drift: Math.random() * 2 - 1, // Zufällige seitliche Drift.
    alpha: 1 // Anfangs-Deckkraft.
  }));

  let frame = 0; // Korrekte Deklaration der Frame-Variable.
  const duration = 120; // Dauer der Animation in Frames (ca. 2 Sekunden bei 60fps).

  // Die Haupt-Zeichenfunktion, die in jedem Animationsframe aufgerufen wird.
  function draw() {
    ctx.clearRect(0, 0, width, height); // Löscht den Canvas-Inhalt des vorherigen Frames.
    pieces.forEach(p => {
      const fadeStart = duration * 0.6; // Zeitpunkt in Frames, ab dem das Konfetti ausblendet.
      if (frame > fadeStart) {
        // Reduziert die Deckkraft, um ein Ausblenden zu simulieren.
        p.alpha = Math.max(0, 1 - (frame - fadeStart) / (duration - fadeStart));
      }

      ctx.beginPath();
      // Zeichnet einen Kreis für jedes Konfetti-Stück.
      ctx.arc(p.x + frame * p.drift, p.y - frame * p.speed, p.r, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = p.alpha; // Wendet die aktuelle Deckkraft an.
      ctx.fill();
      ctx.globalAlpha = 1; // Setzt die globale Deckkraft für nachfolgende Zeichnungen zurück.
    });

    frame++; // 'frame' inkrementieren.
    if (frame < duration) {
      requestAnimationFrame(draw); // Fordert den nächsten Animationsframe an.
    } else {
      canvas.style.display = "none"; // Blendet das Canvas aus, wenn die Animation beendet ist.
    }
  }

  draw(); // Startet die Konfetti-Animation.
}


// Quiz-Komponente zur Anzeige von Fragen und Bearbeitung von Antworten.
// React.memo wird verwendet, um unnötige Neu-Renderings zu vermeiden,
// wenn sich die Props nicht ändern.
const Quiz = React.memo(({ quizData }) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null); // Speichert den Index der aktuell ausgewählten Antwort.
  const [feedbackMessage, setFeedbackMessage] = useState(''); // Speichert die Feedback-Nachricht (Richtig/Falsch).
  const [isCorrect, setIsCorrect] = useState(false); // Verfolgt, ob die richtige Antwort gefunden wurde.
  const [attemptedWrongIndexes, setAttemptedWrongIndexes] = useState([]); // Speichert die Indizes der falsch versuchten Antworten.
  const quizContainerRef = useRef(null); // Referenz auf das DOM-Element des Quiz-Containers.
  const canvasRef = useRef(null); // Referenz auf das DOM-Element des Canvas.

  // Setzt den Quiz-Zustand zurück, wenn sich die Quiz-Daten (z.B. bei Kapitelwechsel) ändern.
  useEffect(() => {
    setSelectedOptionIndex(null);
    setFeedbackMessage('');
    setIsCorrect(false);
    setAttemptedWrongIndexes([]); // Setzt die Liste der falschen Versuche zurück.
  }, [quizData]); // Abhängigkeit von quizData stellt sicher, dass der Reset beim Kapitelwechsel erfolgt.

  // Startet Konfetti, wenn die Antwort korrekt ist und das Canvas-Element verfügbar ist.
  useEffect(() => {
    if (isCorrect && canvasRef.current) { // Prüft, ob isCorrect true ist UND Canvas-Element existiert.
      launchConfettiOverBox(canvasRef.current); // Canvas-Element direkt übergeben.
    }
  }, [isCorrect]); // Dieser useEffect reagiert nur auf Änderungen in isCorrect.

  // Behandelt das Klicken auf eine Antwortoption.
  const handleOptionClick = (index) => {
    if (isCorrect) return; // Wenn die Antwort bereits korrekt ist, verhindere weitere Klicks.
    setSelectedOptionIndex(index);
    setFeedbackMessage(''); // Löscht vorheriges Feedback, wenn eine neue Option ausgewählt wird.
  };

  // Behandelt das Absenden der Antwort.
  const handleSubmit = () => {
    if (selectedOptionIndex === null) {
      setFeedbackMessage('Bitte wähle eine Antwort aus.');
      return;
    }

    if (selectedOptionIndex === quizData.correctAnswerIndex) {
      setFeedbackMessage('Richtig! Gut gemacht!');
      setIsCorrect(true); // Setzt den Zustand auf korrekt, was den useEffect oben triggert.
    } else {
      setFeedbackMessage('Falsch. Bitte versuche es noch einmal.');
      // Fügt die aktuell ausgewählte (falsche) Option zur Liste der falschen Versuche hinzu.
      setAttemptedWrongIndexes(prev => [...prev, selectedOptionIndex]);
      setSelectedOptionIndex(null); // Löscht die Auswahl für den nächsten Versuch.
    }
  };

  return (
    <div ref={quizContainerRef} className="bg-[#Faf6f4] p-6 rounded-lg shadow-inner mt-4 relative overflow-hidden border border-gray-200">
      <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full pointer-events-none"></canvas>
      <h4 className="text-xl text-gray-800 mb-4 font-open-sans font-normal">{quizData.question}</h4> {/* Wendet die Open Sans-Schriftart auf die Frage an. */}
      <div className="space-y-3">
        {quizData.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleOptionClick(index)}
            className={`w-full text-left p-3 rounded-md border-2 transition-colors duration-300 font-open-sans font-normal
              ${selectedOptionIndex === index && !isCorrect ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white'} /* Styling für ausgewählte Antwort (vor dem Absenden). */
              ${isCorrect && index === quizData.correctAnswerIndex ? 'bg-green-100 border-green-500 ring-4 ring-green-300' : ''} /* Styling für die korrekte Antwort nach dem Absenden. */
              ${!isCorrect && attemptedWrongIndexes.includes(index) ? 'bg-red-100 border-red-500' : ''} /* Styling für falsch versuchte Antworten. */
              ${isCorrect ? 'cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'} /* Cursor und Hover-Effekt. */
            `}
            disabled={isCorrect} // Deaktiviert alle Buttons, sobald die richtige Antwort gefunden wurde.
          >
            {option}
          </button>
        ))}
      </div>
      {!isCorrect && ( // Zeigt den Absende-Button nur an, wenn die Antwort noch nicht korrekt ist.
        <button
          onClick={handleSubmit}
          className="mt-6 px-6 py-3 rounded-full transition-colors shadow-md font-open-sans bg-[#008080] text-white hover:bg-[#006666] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50"
        >
          Antwort absenden
        </button>
      )}
      {feedbackMessage && ( // Zeigt die Feedback-Nachricht an.
        <p className={`mt-4 text-lg font-open-sans ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
          {feedbackMessage}
        </p>
      )}
    </div>
  );
}); // Ende von React.memo() für die Quiz-Komponente.

// Hauptkomponente der Anwendung, die die Geschichte und Navigation verwaltet.
const App = () => {
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0); // Zustand für den aktuellen Kapitelindex.
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Zustand für die Sichtbarkeit des Hamburger-Menüs.
  const [isInfoOpen, setIsInfoOpen] = useState(false); // Zustand für die Sichtbarkeit der Info-Seite.
  const mainContentRef = useRef(null);

  // Scrollt beim Kapitelwechsel automatisch zum Seitenanfang und schließt das Menü.
  useEffect(() => {
    // Scrolle den <main>-Container nach oben
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
    // Scrolle zusätzlich das Hauptfenster, was auf Mobilgeräten hilft
    window.scrollTo(0, 0);
    setIsMenuOpen(false);
  }, [currentChapterIndex]); // Dieser Effekt wird bei jeder Änderung von currentChapterIndex ausgelöst.

  // Definition der gesamten Geschichte "Marie und das Geld" mit Kapiteln und deren Inhalten.
  const marieStory = {
    id: 'marie-geld',
    title: 'Marie und das Geld', // Haupttitel der Geschichte.
    description: 'Eine Geschichte über ein 7-jähriges Mädchen, das Taschengeld, Sparen und Banken versteht, mit interaktiven Quizfragen.', // Kurzbeschreibung der Geschichte.
    chapters: [
      {
        title: 'Marie und das Geld',
        isCoverPage: true,      // Flag for fullscreen image
        hideFromContents: true, // Flag to hide from chapter list
        content: [
          {
            "type": "animation-large",
            "src": "/animations/0.gif",
            "alt": "Animation von Marie"
          },
        ]
      },
      {
        "title": "1. Marie bekommt Taschengeld",
        "content": [
          {
            "type": "paragraph",
            "text": "Das ist Marie. Sie ist 7 Jahre alt und bekommt von ihren Eltern Taschengeld. Das bekommt sie, damit sie etwas sehr Wichtiges lernt."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 1, Bild 1 - Marie.jpg",
            "alt": "Bild von Marie"
          },
          {
            "type": "quiz",
            "question": "Warum bekommt Marie Taschengeld?",
            "options": [
              "a) Damit sie es in die Tasche steckt.",
              "b) Damit sie den Umgang mit Geld lernt."
            ],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "2. Was ist Geld?",
        "content": [
          {
            "type": "paragraph",
            "text": "Geld ist ein Tauschmittel. Der Wert der gegen Geld eingetauschten Güter wird in Zahlen ausgedrückt. Zum Beispiel: Wenn Marie eine Kugel Eis kaufen möchte, braucht sie dafür 1,50 Euro."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 2, Bild 1 - Eisverkaeufer.jpg",
            "alt": "Bild eines Eisverkäufers"
          },
          {
            "type": "quiz",
            "question": "Was kann man noch gegen Geld eintauschen?",
            "options": [
              "a) Freunde",
              "b) Familie",
              "c) Dienstleistungen"
            ],
            "correctAnswerIndex": 2
          }
        ]
      },
      {
        "title": "3. Wie war es früher?",
        "content": [
          {
            "type": "paragraph",
            "text": "Früher haben die Menschen mit Steinen, Tieren oder Gold bezahlt. Heute bezahlen wir mit Bargeld, welches von Zentralbanken hergestellt und ausgegeben wird."
          },
          {
            "type": "animation",
            "src": "/animations/3.gif",
            "alt": "Animation eines Huhns"
          },
          {
            "type": "quiz",
            "question": "Wie heißt die deutsche Zentralbank?",
            "options": [
              "a) Deutsche Bundesbank",
              "b) Deutsche Zentralbank",
              "c) Zentrale Bundesbank"
            ],
            "correctAnswerIndex": 0
          }
        ]
      },
      {
        "title": "4. Was ist die Währung in Deutschland?",
        "content": [
          {
            "type": "paragraph",
            "text": "Als Währung bezeichnet man das Geld, welches in einem bestimmten Land gegen Güter ausgetauscht wird. Der Euro ist die Währung der Europäischen Union; er gilt also nicht nur in Deutschland, sondern auch in anderen europäischen Staaten."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 5, Bild 1 - 1 Euro, 50 Cent.jpg",
            "alt": "Bild einer 1-Euro-Münze und einer 50-Cent-Münze"
          },
          {
            "type": "quiz",
            "question": "Wie sieht die Flagge der Europäischen Union aus?",
            "options": [
              "a) Ein blauer Hintergrund mit 12 im Kreis angeordneten Sternen.",
              "b) Ein blauer Hintergrund mit gelben Globus in der Mitte."
            ],
            "correctAnswerIndex": 0
          }
        ]
      },
      {
        "title": "5. Wie sehen die Euromünzen und Euroscheine aus?",
        "content": [
          {
            "type": "paragraph",
            "text": "Ein Euro ist gleich 100 Cents."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 5, Bild 3 - 50, 10, 5, 1 Euro.jpg",
            "alt": "Bild einer 1-Euro-Münze und einer 50-Cent-Münze"
          },
          {
            "type": "paragraph",
            "text": "Auf dieser Website kannst du dir alle Euroscheine ansehen:"
          },
          {
            "type": "link",
            "text": "Euro-Banknoten | Deutsche Bundesbank",
            "href": "https://www.bundesbank.de/de/aufgaben/bargeld/euro-banknoten"
          },
          {
            "type": "paragraph",
            "text": "Und auf dieser Website kannst du dir alle Euro-Münzen ansehen:"
          },
          {
            "type": "link",
            "text": "Euro-Münzen | Deutsche Bundesbank",
            "href": "https://www.bundesbank.de/de/aufgaben/bargeld/euro-muenzen"
          },
          {
            "type": "quiz",
            "question": "Welche Euroscheine werden heute noch ausgegeben?",
            "options": [
              "a) 5, 10, 20, 50, 100",
              "b) 5, 10, 20, 50, 100, 200",
              "c) 5, 10, 20, 50, 100, 200, 500"
            ],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "6. Andere Währungen und deren Umrechnung",
        "content": [
          {
            "type": "paragraph",
            "text": "In vielen Ländern außerhalb der Europäischen Union kann man nicht mit dem Euro bezahlen."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 6, Bild 1 - Familienfoto.jpg",
            "alt": "Neues Familienbild"
          },
          {
            "type": "paragraph",
            "text": "Bei anderen Währungen muss man die häufig wechselnden Wechselkurse beachten, denn unsere Euros müssen zu einem bestimmten Preis in die andere Währung umgetauscht werden."
          },
          {
            "type": "animation",
            "src": "/animations/6.gif",
            "alt": "Animation eines Taschenrechners",
            "size": "max-w-full h-40 max-h-80 object-contain"
          },
          {
            "type": "quiz",
            "question": "Womit zahlt man in der Schweiz?",
            "options": ["a) Euro", "b) Schweizer Franken"],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "7. Wie verdient man Geld?",
        "content": [
          {
            "type": "paragraph",
            "text": "Die meisten Leute arbeiten entweder für ein Unternehmen, den deutschen Staat oder sie sind selbständig. Maries Vater ist Polizist und arbeitet für den deutschen Staat."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 7, Bild 2 - Beruf des Vaters.jpg",
            "alt": "Bild des Berufs des Vaters"
          },
          {
            "type": "paragraph",
            "text": "Der Bäcker, bei dem Marie ihre Brötchen kauft, ist Unternehmer und somit selbstständig. Die dort arbeitende Verkäuferin ist Arbeitnehmerin."
          },
          {
            "type": "animation",
            "src": "/animations/7.gif",
            "alt": "Animation eines Bäckers"
          },
          {
            "type": "quiz",
            "question": "Für wen oder was arbeitet die Verkäuferin?",
            "options": [
              "a) Den Bäcker",
              "b) Das Unternehmen",
              "c) Den deutschen Staat"
            ],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "8. Was kann man alles für Geld kaufen?",
        "content": [
          {
            "type": "paragraph",
            "text": "Es gibt viele Dinge, die man sich nicht für Geld kaufen kann, zum Beispiel: Sonnenschein, Familie oder Liebe. Auch gute Freunde sind, wie ein Sprichwort sagt, “unbezahlbar”. Das bedeutet, dass sie nicht käuflich erwerblich sind."
          },
          {
            "type": "animation",
            "src": "/animations/8.gif",
            "alt": "Geld ist nicht alles Animation"
          },
          {
            "type": "quiz",
            "question": "Welche der folgenden Antwortmöglichkeiten kann man nicht für Geld kaufen?",
            "options": [
              "a) Ein Gefühl von Zufriedenheit",
              "b) Ein Haustier",
              "c) Eine Therapeutische Behandlung"
            ],
            "correctAnswerIndex": 0
          }
        ]
      },
      {
        "title": "9. Was ist Recht?",
        "content": [
          {
            "type": "paragraph",
            "text": "Recht sind die Regeln, die für alle Menschen in einem Land gelten; in Deutschland werden diese durch die vom Volk gewählten Politiker im Bundestag beschlossen. Du kannst alle Gesetze auf dieser Website finden:"
          },
          {
            "type": "link",
            "text": "Gesetze im Internet",
            "href": "https://www.gesetze-im-internet.de/"
          },
          {
            "type": "animation",
            "src": "/animations/9.gif",
            "alt": "Animation Marie vor dem Bundestagsgebaeude"
          },
          {
            "type": "quiz",
            "question": "In welchem Gesetzbuch kann man nachlesen, was ein Arbeitnehmer ist?",
            "options": [
              "a) Im Grundgesetz",
              "b) Im Handelsgesetzbuch",
              "c) Im Bürgerlichen Gesetzbuch"
            ],
            "correctAnswerIndex": 2
          }
        ]
      },
      {
        "title": "10. Geschäftsfähigkeit ab 18 Jahren",
        "content": [
          {
            "type": "paragraph",
            "text": "Wenn man 18 Jahre alt ist, ist man nach § 2 BGB voll geschäftsfähig."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 10, Bild 1 - Geschaeftsfaehiger Bruder.jpg",
            "alt": "Bild eines geschäftsfähigen Bruders"
          },
          {
            "type": "quiz",
            "question": "Braucht man als voll geschäftsfähige Person noch die Einverständniserklärung der Eltern, um bestimmte Verträge abzuschließen?",
            "options": ["a) Ja", "b) Nein"],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "11. Geschäftsunfähigkeit bis 7 Jahre",
        "content": [
          {
            "type": "paragraph",
            "text": "Kinder bis zu 7 Jahren sind nach § 104 BGB geschäftsunfähig. Wenn Kinder unter 7 Jahren Verträge abschließen, sind diese nach § 105 BGB nichtig."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 11, Bild 1 - Geschaeftsunfaehiger Bruder.jpg",
            "alt": "Bild eines geschäftsunfähigen Bruders"
          },
          {
            "type": "quiz",
            "question": "Was bedeutet das?",
            "options": [
              "a) Bei Kindern unter 7 Jahren müssen immer die Eltern die geschäftlichen Sachen regeln.",
              "b) Bei Kindern unter 7 Jahren ist bekannt, dass sie noch nicht so gut darin sind, Verträge abzuschließen."
            ],
            "correctAnswerIndex": 0
          }
        ]
      },
      {
        "title": "12. Beschränkte Geschäftsfähigkeit zwischen 7 und 18 Jahren",
        "content": [
          {
            "type": "paragraph",
            "text": "Marie ist 7 Jahre alt und daher nach § 106 BGB beschränkt geschäftsfähig. Nach § 110 BGB darf sie Geschäfte abschließen, die sie von ihrem Taschengeld bezahlen kann."
          },
          {
            "type": "animation",
            "src": "/animations/12.gif",
            "alt": "Animation von Marie"
          },
          {
            "type": "quiz",
            "question": "Was bedeutet das für Marie?",
            "options": [
              "a) Für den Abschluss von Geschäften darf Marie nur das Geld benutzen, was sich gerade in ihrer Tasche befindet.",
              "b) Für die Einkäufe, die Marie nicht von ihrem Taschengeld bezahlen kann, braucht sie ihre Eltern."
            ],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "13. Einnahmen und Ausgaben",
        "content": [
          {
            "type": "paragraph",
            "text": "Wenn Marie Geld einnimmt, z.B. durch Taschengeld und Geschenke von ihrer Oma, dann nennt man das Einnahmen. Das, was Marie für ihre Käufe bezahlen muss, nennt man Ausgaben. Wenn man mehr Geld ausgibt, als man einnimmt, hat man einen Verlust."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 14, Bild 1 - Sparschwein.jpg",
            "alt": "Bild eines Sparschweins"
          },
          {
            "type": "quiz",
            "question": "Wie nennt man es, wenn Geld übrig bleibt, nachdem man die Summe der Ausgaben von der Summe der Einnahmen abgezogen hat?",
            "options": [
              "a) Restmenge",
              "b) Überschuss",
              "c) Überbleibsel"
            ],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "14. Was ist ein Konto?",
        "content": [
          {
            "type": "paragraph",
            "text": "Auf einem Girokonto verbuchen Banken und Sparkassen alle Zahlungseingänge und Zahlungsausgänge für einen bestimmten Kontoinhaber. Die Buchung läuft elektronisch."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 18 - Bild 1 - Girokarte v1.jpg",
            "alt": "Bild von Marie beim Fahrradkauf mit Eltern"
          },
          {
            "type": "quiz",
            "question": "Was ist Buchgeld?",
            "options": [
              "a) Buchgeld ist das Geld auf einem Bankkonto.",
              "b) Buchgeld ist ein anderer Name für Bargeld."
            ],
            "correctAnswerIndex": 0
          }
        ]
      },
      {
        "title": "15. Bank oder Sparkasse",
        "content": [
          {
            "type": "paragraph",
            "text": "Banken und Sparkassen sind Unternehmen, die Dienstleistungen im Zusammenhang mit Geld anbieten. Dazu gehören: Einzahlungen, Auszahlungen und Überweisungen von Geld, sowie Kreditaufnahmen und Währungstausch."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 16, Bild 1 - Marie vor der Bank.png",
            "alt": "Marie vor Bank"
          },
          {
            "type": "quiz",
            "question": "Was bedeutet es, einen Kredit aufzunehmen?",
            "options": [
              "a) Das ist der Prozess, durch den man gehen muss, um eine Kreditkarte zu beantragen.",
              "b) Die Bank stellt einem Geld zu Verfügung, welches man später in Raten und mit Zinsen zurückzahlen muss."
            ],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "16. Girokonto, Sparkonto, Depot",
        "content": [
          {
            "type": "paragraph",
            "text": "Erwachsene haben in der Regel drei verschiedene Konten: Ein Girokonto, ein Sparkonto und ein Depot für Wertpapiere."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 17, Bild 1 - Eltern vor der Bank.jpg",
            "alt": "Bild von Eltern vor der Bank"
          },
          {
            "type": "quiz",
            "question": "Wie heißt der Betrag, den Erwachsene für ihr Girokonto bezahlen müssen?",
            "options": [
              "a) Abonnement",
              "b) Gebühr",
              "c) Steuer"
            ],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "17. Karten",
        "content": [
          {
            "type": "paragraph",
            "text": "Es gibt Girokarten, Kreditkarten und Prepaid-Karten. Mit Girokarten werden Zahlungen direkt – und sofort – vom Konto abgebucht. Mit Kreditkarten können Erwachsene einen kurzzeitigen Kredit aufnehmen; dabei werden die Zahlungen in regelmäßigen Abständen vom Girokonto eingezogen. Mit Prepaid-Karten kann nur so viel Geld ausgegeben werden, wie vorher auf die Karte elektronisch aufgeladen wurde."
          },
          {
            "type": "image",
            "src": "/images/Kapitel 18, Bild 1 - Kreditkarte.jpg",
            "alt": "Bild einer Kreditkarte"
          },
          {
            "type": "quiz",
            "question": "Was ist ein Verfügungsrahmen?",
            "options": [
              "a) Das ist der Betrag, der vorher mit dem Kreditkartenanbieter und der Bank vereinbart wurde; er hängt von der Kreditwürdigkeit des Kreditkarteninhabers ab.",
              "b) Das ist der Betrag, der sich auf dem Girokonto des Kreditkarteninhabers befindet. Übersteigt der Kredit diesen Rahmen, wird er nicht gewährt.",
              "c) Das ist der Zeitrahmen, in dem der Kreditkarteninhaber seine Kreditkarte nutzen kann. Danach ist die Karte ungültig."
            ],
            "correctAnswerIndex": 0
          }
        ]
      },
      {
        "title": "18. Einkommenssteuer",
        "content": [
          {
            "type": "paragraph",
            "text": "Auf Einkommen muss man nach dem Einkommensteuergesetz Steuern zahlen. Mithilfe dieser Steuern baut unser Staat zum Beispiel Straßen, Schulen und Krankenhäuser."
          },
          {
            "type": "animation",
            "src": "/animations/18.gif",
            "alt": "Animation von Marie, Straße, Schule, Krankenhaus"
          },
          {
            "type": "quiz",
            "question": "Was ist der Grundfreibetrag?",
            "options": [
              "a) Das ist ein Steuerbetrag, der abgerichtet werden muss, bevor Steuererleichterungen geltend gemacht werden können.",
              "b) Das ist ein festgelegter Betrag, den jeder Arbeitnehmer als Geschenk vom deutschen Staat erhalten kann.",
              "c) Wenn das Einkommen einer Person einen bestimmten Betrag nicht übersteigt, dann muss diese keine Einkommensteuer bezahlen."
            ],
            "correctAnswerIndex": 2
          }
        ]
      },
      {
        "title": "19. Ausblick",
        "content": [
          {
            "type": "paragraph",
            "text": "Maries großer Bruder Adrian investiert bei seinen Geldanlagen auch in Wertpapiere; das sind Anleihen, Aktien, Fondsanteile und ETFs – letzteres sind Fonds, die an der Börse gehandelt werden. Marie möchte, genau wie er, auch irgendwann ein Depot haben, in das sie Wertpapiere legen kann."
          },
          {
            "type": "animation",
            "src": "/animations/19.gif",
            "alt": "Animation von Großem Bruder, Marie und ETFs"
          },
          {
            "type": "quiz",
            "question": "Was bedeutet es, wenn man davon spricht, dass Geld an der Börse “wächst”?",
            "options": [
              "a) Die Kosten einzelner Unternehmensanteile werden geringer, je mehr Geld diese abwerfen.",
              "b) Durch Investitionen in die Fonds von Unternehmen können diese wachsen und werfen mehr Gewinn ab."
            ],
            "correctAnswerIndex": 1
          }
        ]
      },
      {
        "title": "Du möchtest mehr wissen?",
        "content": [
          {
            "type": "paragraph",
            "text": "Dann wirf doch einen Blick ins Buch! Marie und das Geld ist ein Kinderbuch aus der Reihe Recht und Geld von der Autorin Rosa-Maria Krämer und vermittelt Kindern ein Grundwissen über Geld als Tauschmittel, den Euro und die Zentralbanken."
          },
          {
            "type": "link",
            "text": "Hier kannst du es kaufen",
            "href": "https://www.amazon.de/Marie-das-Geld-Rosa-Maria-Kr%C3%A4mer/dp/B0FDB578FC?source=ps-sl-shoppingads-lpcontext&psc=1&smid=A3JWKAKR8XB7XF&language=de_DE"
          },
          {
            "type": "paragraph",
            "text": "– dich erwarten eine Vielzahl an Illustrationen, längere Kapitel und neue Quizfragen!"
          },
          {
            "type": "animation",
            "src": "/animations/1.gif",
            "alt": "Animation von Marie"
          },
        ]
      },
      {
        "title": "Impressum",
        "hideFromContents": true,
        "content": [
          { "type": "paragraph", "text": "<strong>Angaben gemäß § 5 DDG</strong>" },
          { "type": "paragraph", "text": "Rosa-Maria Krämer<br />Wöllstädter Str. 4<br />60385 Frankfurt" },
          { "type": "paragraph", "text": "<strong>Kontakt:</strong>" },
          { "type": "paragraph", "text": "Telefon: <a href='tel:+496994599849' class='text-[#008080] hover:text-[#006666] underline'>0049 (0)69 94- 59 98 49</a><br />E-Mail: <a href='mailto:RosaKra@t-online.de' class='text-[#008080] hover:text-[#006666] underline'>RosaKra@t-online.de</a>" },
          { "type": "paragraph", "text": "<strong>Inhaltlich verantwortlich gemäß § 18 Abs. 2 MStV</strong>" },
          { "type": "paragraph", "text": "Rosa-Maria Krämer, Wöllstädter Str. 4, 60385 Frankfurt" },
          { "type": "paragraph", "text": "<strong>Haftungsausschluss:</strong>" },
          { "type": "paragraph", "text": "<strong>Haftung für Inhalte</strong><br />Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen." },
          { "type": "paragraph", "text": "<strong>Haftung für Links</strong><br />Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen." },
          { "type": "paragraph", "text": "<strong>Datenschutz</strong>" },
          { "type": "paragraph", "text": "Die Nutzung unserer Webseite ist in der Regel ohne Angabe personenbezogener Daten möglich. Soweit auf unseren Seiten personenbezogene Daten (beispielsweise Name, Anschrift oder eMail-Adressen) erhoben werden, erfolgt dies, soweit möglich, stets auf freiwilliger Basis. Diese Daten werden ohne Ihre ausdrückliche Zustimmung nicht an Dritte weitergegeben." },
          { "type": "paragraph", "text": "Wir weisen darauf hin, dass die Datenübertragung im Internet (z.B. bei der Kommunikation per E-Mail) Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist nicht möglich." },
          { "type": "paragraph", "text": "Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten durch Dritte zur Übersendung von nicht ausdrücklich angeforderter Werbung und Informationsmaterialien wird hiermit ausdrücklich widersprochen. Die Betreiber der Seiten behalten sich ausdrücklich rechtliche Schritte im Falle der unverlangten Zusendung von Werbeinformationen, etwa durch Spam-Mails, vor." },
          { "type": "paragraph", "text": "<strong>Text:</strong> Rosa-Maria Krämer" },
          { "type": "paragraph", "text": "<strong>Illustrationen & Entwicklung:</strong> H.O.P.E. – humans on planet earth UG" }
        ]
      }
    ],
  };


  const currentChapter = marieStory.chapters[currentChapterIndex];
  const isFirstChapter = currentChapterIndex === 0;
  const isLastChapter = marieStory.chapters.length - 1 === currentChapterIndex;

  // Handler für das vorherige Kapitel.
  const handlePreviousChapter = () => {
    if (!isFirstChapter) {
      setCurrentChapterIndex(currentChapterIndex - 1);
    }
  };

  // Handler für das nächste Kapitel.
  const handleNextChapter = () => {
    if (!isLastChapter) {
      setCurrentChapterIndex(currentChapterIndex + 1);
    }
  };

  // Handler für die Auswahl eines Kapitels aus dem Menü.
  const handleChapterSelect = (index) => {
    setCurrentChapterIndex(index);
    setIsMenuOpen(false); // Menü schließen nach Auswahl.
  };

  return (
    // Äußerer Container, der das "Telefon"-Layout auf großen Bildschirmen erstellt.
    <div className="flex justify-center items-center bg-gray-100 md:bg-white md:p-8">
      {/* Hintergrund für info popup */}
      {isInfoOpen && (
        <div
          className="absolute inset-0 flex justify-center items-center z-50 "
        >
        </div>
      )}

      {/* Simulierter Notch/Kamera-Bereich für das Telefon-Layout */}
      <div className="relative w-full h-full md:w-[414px] md:h-[896px] md:rounded-[3rem] md:shadow-2xl md:border-[10px] md:border-gray-800 md:overflow-hidden bg-white flex flex-col">
        {isInfoOpen && (
          <div className="absolute inset-0 flex justify-center items-center z-50" onClick={() => setIsInfoOpen(false)}>
            <InfoPage onClose={() => setIsInfoOpen(false)} />
          </div>
        )}

        <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap" rel="stylesheet" />
        <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-xl z-50"></div>
        <header className="relative pt-8 pb-4 px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`fixed md:absolute top-4 left-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#008080] z-50 ${isMenuOpen || isInfoOpen ? 'hidden' : ''}`}
            aria-label="Menü öffnen"
          >
            <svg className="h-8 w-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <button
            onClick={() => setIsInfoOpen(!isInfoOpen)}
            className={`fixed md:absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#008080] z-50 ${isMenuOpen || isInfoOpen ? 'hidden' : ''}`}
            aria-label="Info öffnen"
          >
            <svg className="h-8 w-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </header>

        {/* Sidebar-Menü für die Kapitelauswahl. */}
        <div className={`fixed inset-y-0 left-0 w-full bg-white shadow-lg transform ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out z-40 flex flex-col md:absolute md:rounded-[3rem]`}>
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800 font-open-sans">Inhalt</h3>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#008080]"
              aria-label="Menü schließen"
            >
              <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">
              {marieStory.chapters.map((chapter, index) => {
                if (chapter.hideFromContents) {
                  return null;
                }
                return (
                  <li key={index}>
                    <button
                      onClick={() => handleChapterSelect(index)}
                      className={`block w-full text-left p-2 font-open-sans text-lg transition-colors ${index === currentChapterIndex ? 'text-[#008080] font-bold bg-green-50' : 'text-gray-700 hover:text-[#008080] hover:bg-gray-50'}`}
                    >
                      {chapter.title}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        {isMenuOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-30 md:absolute md:rounded-[3rem]" onClick={() => setIsMenuOpen(false)}></div>
        )}

        <main ref={mainContentRef} className="flex-grow overflow-y-auto px-10 sm:px-6 lg:px-10 pb-20 flex flex-col">
          <h2 className="text-2xl sm:text-3xl font-normal text-gray-800 text-left pt-8 pb-3">
            {currentChapter.title}
          </h2>
          <div className="flex-grow space-y-4 text-base text-gray-700 leading-relaxed font-open-sans font-normal">
            {currentChapter.content.map((block, index) => {
              const key = `${currentChapterIndex}-${index}`;
              if (block.type === 'paragraph') {
                return <p key={index} dangerouslySetInnerHTML={{ __html: block.text }}></p>;
              } else if (block.type === 'image') {
                const sizeClass = block.size || 'max-w-full h-auto max-h-80 object-contain';
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className={sizeClass}
                      onError={(e) => { e.target.onerror = null; e.target.src = `/images/placeholder.png`; }}
                    />
                  </div>
                );
              } else if (block.type === 'image-large') {
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className="max-w-full h-auto max-h-full object-contain"
                      onError={(e) => { e.target.onerror = null; e.target.src = `/images/placeholder.png`; }}
                    />
                  </div>
                );
              } else if (block.type === 'animation') {
                const sizeClass = block.size || 'max-w-full h-auto max-h-80 object-contain';
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className={sizeClass}
                    />
                  </div>
                );
              } else if (block.type === 'animation-large') {
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className="max-w-full h-auto max-h-full object-contain"
                      onError={(e) => { e.target.onerror = null; e.target.src = `/images/placeholder.png`; }}
                    />
                  </div>
                );
              } else if (block.type === 'link') {
                return (
                  <span key={key}>
                    <a href={block.href} target="_blank" rel="noopener noreferrer" className="text-[#008080] hover:underline font-semibold">
                      {block.text}
                    </a>
                    {' '}
                  </span>
                );
              } else if (block.type === 'customTable') {
                return <Table key={index} data={block.data} />;
              } else if (block.type === 'quiz') {
                return <Quiz key={index} quizData={block} />;
              }
              return null;
            })}
          </div>
          <footer className="text-center pt-8">
            <button
              onClick={() => handleChapterSelect(marieStory.chapters.length - 1)}
              className="text-[#008080] hover:text-[#006666] hover:underline font-open-sans"
            >
              Impressum
            </button>
          </footer>
        </main>

        <div className="fixed md:absolute bottom-0 left-0 right-0 p-4 flex justify-between items-center z-20 pointer-events-none">
          <button
            onClick={handlePreviousChapter}
            disabled={isFirstChapter}
            className={`p-3 rounded-full transition-colors bg-[#008080] text-white pointer-events-auto ${isFirstChapter ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'hover:bg-[#006666] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50'}`}
            aria-label="Vorheriges Kapitel"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <a
            href="https://www.amazon.de/Marie-das-Geld-Rosa-Maria-Kr%C3%A4mer/dp/B0FDB578FC?source=ps-sl-shoppingads-lpcontext&psc=1&smid=A3JWKAKR8XB7XF&language=de_DE"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 rounded-full transition-colors shadow-md font-open-sans bg-yellow-400 text-gray-900 hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50 pointer-events-auto font-bold"
          >
            Buch auf Amazon
          </a>
          <button
            onClick={handleNextChapter}
            disabled={isLastChapter}
            className={`p-3 rounded-full transition-colors bg-[#008080] text-white pointer-events-auto ${isLastChapter ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'hover:bg-[#006666] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50'}`}
            aria-label="Nächstes Kapitel"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;
